import React, { useContext, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Alert, Spinner } from 'react-bootstrap';
import { AuthContext } from '../context/AuthContext';
import NotFoundPage from '../pages/NotFound';
import api from '../api/client';

/**
 * AdminRoute - A component that wraps routes only admin users should access.
 * If the user is logged out, it redirects to login; if the user is not admin, it shows an access denied page.
 *
 * @component
 * @example
 * <AdminRoute>
 *   <SomeAdminOnlyComponent />
 * </AdminRoute>
 *
 * @param {Object} props - The props for the component.
 * @param {React.ReactNode} props.children - The child components to render if the user is an admin.
 * @returns {React.ReactNode} - The child components, an access denied page, or a redirect to login.
 */
const AdminRoute = ({ children }) => {
  // Get the token from AuthContext to determine if the user is authenticated.
  const { token } = useContext(AuthContext);

  // State hooks to manage admin permission checks.
  const [isCheckingRole, setIsCheckingRole] = useState(Boolean(token)); // State to track role loading
  const [isAdmin, setIsAdmin] = useState(false); // State to track whether the user is an admin
  const [error, setError] = useState(''); // Store role check error messages

  /**
   * useEffect - Loads the current user and checks their trusted backend role.
   */
  useEffect(() => {
    const checkAdminRole = async () => {
      if (!token) {
        setIsCheckingRole(false);
        setIsAdmin(false);
        return;
      }

      setIsCheckingRole(true);
      setError('');

      try {
        // Fetch the current user through the refresh-aware client so expired access tokens can renew.
        const response = await api.get('/api/v1/identity/users/me');

        // Only admin users can view admin-only dashboard mockup routes.
        setIsAdmin(response.data?.role?.toLowerCase() === 'admin');
      } catch (err) {
        setError('Unable to verify your account permissions.');
        setIsAdmin(false);
      } finally {
        setIsCheckingRole(false);
      }
    };

    checkAdminRole();
  }, [token]);

  // Redirect logged-out users to login.
  if (!token) {
    return <Navigate to="/login" replace />;
  }

  // Show a small loading state while the backend role check is running.
  if (isCheckingRole) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100 w-100">
        <Alert variant="info" className="d-flex align-items-center mb-0">
          <Spinner animation="border" size="sm" className="me-2" />
          Checking permissions...
        </Alert>
      </div>
    );
  }

  // Show an error state if the backend role check failed.
  if (error) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100 w-100">
        <Alert variant="danger" className="mb-0">{error}</Alert>
      </div>
    );
  }

  // Render the route for admins, otherwise show the forbidden page.
  return isAdmin ? children : <NotFoundPage />;
};

export default AdminRoute;
